// Shared cache + helper for lazy YouTube-link resolution.
//
// Multiple components need to know "did the user click this track recently
// and did we resolve a youtube_id for it?":
//   - DJPalAudioPreview (the click handler)
//   - FeedWaveform     (so the waveform can switch from sample → YT URL
//                       when the resolve lands)
//   - MusicPlayer's autoplay path (resolveTrackSrc in resolvePlaybackSrc.js)
//
// Originally the cache lived inside DJPalAudioPreview module-scope. That
// worked for clicks but FeedWaveform couldn't see it, and the autoplay
// path bypassed it entirely. Lifting to a shared util fixes both.
//
// Convention: cache key is `String(trackId)` where trackId is the
// id the server exposes as `id` (clients never see numeric DB ids).

import { entityId } from './entityId';

const _cache = new Map();              // trackId → youtubeId
const _inflight = new Map();           // trackId → Promise<resolve result>

// Duration in seconds from whatever shape the API/track object carries —
// numeric seconds OR the catalog's "m:ss" / "h:mm:ss" text (tad.duration).
// Exported because several call sites were doing `Number(track.duration)`,
// which NaNs on "4:29" and silently dropped the duration from the lazy
// resolve snapshot (the matcher's duration sanity guard then never fires).
export function parseDurationSec(d) {
    if (d == null) return null;
    const s = String(d).trim();
    if (!s) return null;
    if (s.includes(':')) {
        const parts = s.split(':').map(Number);
        if (parts.length === 2) return parts[0] * 60 + parts[1];
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

function _currentDjpalToken() {
    try { return localStorage.getItem('djpal:token') || null; } catch { return null; }
}

export function getCachedYoutubeId(trackId) {
    if (!trackId) return null;
    return _cache.get(String(trackId)) || null;
}

export function setCachedYoutubeId(trackId, youtubeId) {
    if (!trackId || !youtubeId) return;
    const key = String(trackId);
    _cache.set(key, youtubeId);
    try {
        window.dispatchEvent(new CustomEvent('djpal-track-link-resolved', {
            detail: { trackId: key, youtubeId },
        }));
    } catch { /* no window — server-side rendering, ignore */ }
}

// Fire (or share) the lazy-resolve IPC. Returns:
//   - { youtubeId: '...', cached: true } when cache had it
//   - the raw IPC response object when freshly resolved (may have youtubeId)
//   - null when not applicable / errored / timed out
//
// opts.timeoutMs caps the wait. The underlying promise keeps running in
// the background so the cache fills for next callers even if THIS caller
// gave up.
export async function resolveTrackLinkLazy(track, opts = {}) {
    if (!track) { console.warn('[lazyResolveCache] bail: !track'); return null; }
    if (typeof window === 'undefined' || !window.electron?.djpal?.resolveTrackLink) {
        console.warn('[lazyResolveCache] bail: ipc handler missing');
        return null;
    }

    const trackId = String(entityId(track) || '').trim();
    if (!trackId) {
        console.warn('[lazyResolveCache] bail: no trackId', { id: track.id });
        return null;
    }

    // Cache hit.
    const cached = _cache.get(trackId);
    if (cached) {
        return { youtubeId: cached, cached: true };
    }

    // No bail when the track has no sample: sample-less tracks (typically
    // ones without artwork / Apple match) are still usually resolvable via
    // YouTube search. `src` presence is only a matcher hint (hasAppleId).
    const src = track.previewUrl || track.sampleUrl || track.preview_url || track.preview;

    // Share an inflight promise — two callers (DJPalAudioPreview + autoplay)
    // hitting this for the same track at once shouldn't double-fire.
    const existing = _inflight.get(trackId);
    if (existing) {
        return opts.timeoutMs ? _withTimeout(existing, opts.timeoutMs) : existing;
    }

    const promise = window.electron.djpal.resolveTrackLink({
        trackId,
        artist: track.artist || '',
        title: track.title || '',
        durationSec: parseDurationSec(track.durationSec ?? track.duration),
        hasAppleId: !!src,
        token: opts.token ?? _currentDjpalToken(),
    }).then((result) => {
        const ytId = result?.youtubeId || null;
        if (ytId) setCachedYoutubeId(trackId, ytId);
        return result || null;
    }).catch((e) => {
        console.warn('[lazyResolveCache] IPC rejected', { trackId, err: e?.message || String(e) });
        return null;
    });

    _inflight.set(trackId, promise);
    promise.finally(() => _inflight.delete(trackId));

    return opts.timeoutMs ? _withTimeout(promise, opts.timeoutMs) : promise;
}

// Race a promise against a timeout. On timeout, returns null; the
// underlying promise keeps running so its cache write still lands.
function _withTimeout(promise, ms) {
    let to;
    const timer = new Promise((resolve) => { to = setTimeout(() => resolve(null), ms); });
    return Promise.race([promise.finally(() => clearTimeout(to)), timer]);
}
